import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField } from "@/components/shared/FormFields";
import SpeakerButton from "@/components/ui/SpeakerButton";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import useSpeechRecognition from "@/hooks/useSpeechRecognition";
import { cn } from "@/utils/utils";

/* =====================================================
   PRONUNCIATION EXERCISE – FINAL FIXED FULL VERSION
===================================================== */

export default function PronunciationExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= QUESTIONS ================= */
  const questions = Array.isArray(exercise.pronunciationQuestions)
    ? exercise.pronunciationQuestions.map((q) => ({
        textToPronounce: q.textToPronounce?.trim() || "",
        ipaSound: q.ipaSound?.trim() || "",
        requireCapitalization: !!q.requireCapitalization,
      }))
    : [];

  /* ================= SPEECH ================= */
  const recognition = useSpeechRecognition() || {};

  const {
    transcript = "",
    listening = false,
    startListening = () => {},
    stopListening = () => {},
    resetTranscript = () => {},
    browserSupportsSpeechRecognition = false,
  } = recognition;

  const speechSupported =
    typeof window !== "undefined" &&
    (browserSupportsSpeechRecognition ||
      "SpeechRecognition" in window ||
      "webkitSpeechRecognition" in window);

  /* ================= STATE ================= */
  const [answers, setAnswers] = useState({});
  const [recordingIndex, setRecordingIndex] = useState(null);
  const [dots, setDots] = useState("");
  const [liveTranscript, setLiveTranscript] = useState("");

  const activeIndexRef = useRef(null);

  /* ================= RESET ON RETRY ================= */
  useEffect(() => {
    setAnswers({});
    setRecordingIndex(null);
    setLiveTranscript("");
    activeIndexRef.current = null;
  }, [exercise._id]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text || "").trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[^\w\s]/g, "").replace(/\s+/g, " ");
  };

  /* ================= WORD CHECK ================= */
  const compareWords = (spoken = "", expected = "", requireCap = false) => {
    const clean = (t) => {
      let s = t.trim();
      if (!requireCap) s = s.toLowerCase();
      return s.replace(/[^\w\s]/g, "");
    };

    const spokenWords = clean(spoken).split(/\s+/);
    const expectedWords = clean(expected).split(/\s+/);

    return expectedWords.map((word, i) => {
      if (spokenWords[i] === word) return { word, status: "correct" };
      if (!spokenWords[i]) return { word, status: "missing" };
      return { word, status: "wrong" };
    });
  };

  /* ================= TEXT TO SPEECH ================= */
  const speakText = (text, lang = "en-GB") => {
    if (typeof window === "undefined") return;
    const synth = window.speechSynthesis;
    if (!synth) return;

    synth.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.9;

    const voices = synth.getVoices();
    const voice =
      voices.find((v) => v.lang === lang) ||
      voices.find((v) => v.lang.startsWith(lang.split("-")[0]));

    if (voice) utterance.voice = voice;

    if (voices.length === 0) {
      synth.onvoiceschanged = () => synth.speak(utterance);
    } else {
      synth.speak(utterance);
    }
  };

  /* ================= MIC PERMISSION ================= */
  useEffect(() => {
    navigator.mediaDevices?.getUserMedia({ audio: true }).catch(() => {});
  }, []);

  /* ================= RECORDING DOTS ================= */
  useEffect(() => {
    if (recordingIndex === null) return setDots("");

    const interval = setInterval(() => {
      setDots((d) => (d.length >= 3 ? "" : d + "."));
    }, 500);

    return () => clearInterval(interval);
  }, [recordingIndex]);

  /* ================= START ================= */
  const handleStartListening = (idx) => {
    if (!speechSupported || listening) return;

    setLiveTranscript("");
    activeIndexRef.current = idx;
    setRecordingIndex(idx);
    resetTranscript();

    startListening({
      continuous: false,
      interimResults: true,
      lang: "en-GB",
    });
  };

  /* ================= STOP ================= */
  const handleStopListening = () => {
    stopListening();
    setRecordingIndex(null);
  };

  /* ================= FINAL TRANSCRIPT ================= */
  useEffect(() => {
    if (!transcript) return;

    if (listening) {
      setLiveTranscript(transcript);
      return;
    }

    const idx = activeIndexRef.current;
    if (idx === null || !questions[idx]) return;

    const q = questions[idx];
    const spokenValue = liveTranscript || transcript;

    const isCorrect =
      normalize(spokenValue, q.requireCapitalization) ===
      normalize(q.textToPronounce, q.requireCapitalization);

    const updated = {
      ...answers,
      [idx]: { spoken: spokenValue, isCorrect },
    };

    setAnswers(updated);

    /* ✅ FIX: always notify parent */
    const allAnswered = Object.keys(updated).length === questions.length;
    const allCorrect = Object.values(updated).every((a) => a.isCorrect);

    onComplete(updated, allAnswered && allCorrect);

    /* attempt save */
    setAttempt({
      exerciseId: exercise._id,
      answers: updated,
    });

    activeIndexRef.current = null;
    setLiveTranscript("");
    resetTranscript();
  }, [transcript, listening]);

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    const updateQuestions = (next) =>
      onUpdate("pronunciationQuestions", next);

    const update = (idx, field, value) => {
      const next = [...questions];
      next[idx] = { ...next[idx], [field]: value };
      updateQuestions(next);
    };

    return (
      <div className="space-y-8">
        {questions.map((q, idx) => (
          <Card key={idx} className="p-6 space-y-4">
            <StringField
              label={`Text ${idx + 1}`}
              value={q.textToPronounce}
              onChange={(v) => update(idx, "textToPronounce", v)}
              required
            />

            {q.textToPronounce && (
              <SpeakerButton text={q.textToPronounce} />
            )}

            <StringField
              label="IPA"
              value={q.ipaSound}
              onChange={(v) => update(idx, "ipaSound", v)}
            />

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) =>
                update(idx, "requireCapitalization", c)
              }
            />
          </Card>
        ))}
      </div>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Pronunciation Practice</CardTitle>
        <CardDescription>Speak clearly</CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, idx) => {
          const result = answers[idx];

          return (
            <div key={idx} className="border rounded-xl p-6 text-center">
              <div className="text-2xl font-bold text-blue-700">
                {q.textToPronounce}
              </div>

              <SpeakerButton text={q.textToPronounce} />

              <div className="flex justify-center gap-3 mt-4">
                {recordingIndex !== idx ? (
                  <Button
                    onClick={() => handleStartListening(idx)}
                    disabled={showResults}
                  >
                    🎤 Speak
                  </Button>
                ) : (
                  <>
                    <Button disabled className="animate-pulse">
                      Recording{dots}
                    </Button>
                    <Button onClick={handleStopListening}>
                      Stop
                    </Button>
                  </>
                )}
              </div>

              {(recordingIndex === idx || result?.spoken) && (
                <div className="mt-4 p-3 border rounded bg-gray-50">
                  {recordingIndex === idx
                    ? liveTranscript || "Listening..."
                    : result?.spoken}
                </div>
              )}

              {showResults && result && (
                <div
                  className={cn(
                    "mt-4 p-4 rounded border",
                    result.isCorrect
                      ? "bg-green-100"
                      : "bg-red-100"
                  )}
                >
                  {result.isCorrect ? "Correct" : "Wrong"}

                  <div className="flex flex-wrap gap-2 mt-2 justify-center">
                    {compareWords(
                      result.spoken,
                      q.textToPronounce,
                      q.requireCapitalization
                    ).map((w, i) => (
                      <span
                        key={i}
                        className={cn(
                          "px-2 py-1 rounded text-sm",
                          w.status === "correct" && "bg-green-200",
                          w.status === "wrong" && "bg-red-200",
                          w.status === "missing" && "bg-yellow-200"
                        )}
                      >
                        {w.word}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}